// CreateOrganization.tsx
import React, { useState } from 'react';
import {
    Drawer,
    Button,
    TextInput,
    Title,
    Box,
    Group,
    Notification,
    LoadingOverlay,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconX } from '@tabler/icons-react';
import {createOrganization, CreateOrganizationData} from "../../../Services/OrganizationService";

type OrganizationFields = {
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
};

interface CreateOrganizationProps {
    onCreateSuccess?: () => void;
}

const CreateOrganization: React.FC<CreateOrganizationProps> = ({ onCreateSuccess }) => {
    const [opened, setOpened] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const form = useForm<OrganizationFields>({
        initialValues: {
            name: '',
            street: '',
            city: '',
            state: '',
            zip: '',
            country: '',
        },
        validate: {
            name: (value) =>
                value.trim().length < 2 ? 'Name muss mindestens zwei zeichen haben' : null,
            street: (value) => (value.trim().length === 0 ? 'Street is required' : null),
            city: (value) => (value.trim().length === 0 ? 'City is required' : null),
            state: (value) => (value.trim().length === 0 ? 'State is required' : null),
            zip: (value) => (value.trim().length === 0 ? 'ZIP is required' : null),
            country: (value) =>
                value.trim().length === 0 ? 'Country is required' : null,
        },
    });

    const handleSubmit = async (values: OrganizationFields) => {
        setLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            // 1) Call our service layer instead of direct axios
            const createdOrg = await createOrganization(values as CreateOrganizationData);
            setSuccessMessage(`Betrieb erstellt: ${createdOrg.name}`);

            // 2) Reset form
            form.reset();

            // 3) Notify parent to refetch the list, if provided
            onCreateSuccess?.();
        } catch (error: any) {
            console.error('Error creating organization:', error);
            setErrorMessage(
                error?.response?.data?.error || 'Fehler'
            );
        } finally {
            setLoading(false);
        }
    };

    const closeDrawer = () => {
        setOpened(false);
        form.reset();
        setErrorMessage(null);
        setSuccessMessage(null);
    };

    return (
        <Box>
            <Button variant="filled" onClick={() => setOpened(true)}>
                Erstelle neuen Betrieb
            </Button>

            <Drawer
                opened={opened}
                onClose={closeDrawer}
                title="Erstelle Betrieb"
                padding="md"
                size="md"
            >
                <Box pos="relative">
                    <LoadingOverlay visible={loading} />

                    {successMessage && (
                        <Notification
                            icon={<IconCheck size={18} />}
                            color="teal"
                            title="Success"
                            onClose={() => setSuccessMessage(null)}
                            mb="md"
                        >
                            {successMessage}
                        </Notification>
                    )}

                    {errorMessage && (
                        <Notification
                            icon={<IconX size={18} />}
                            color="red"
                            title="Error"
                            onClose={() => setErrorMessage(null)}
                            mb="md"
                        >
                            {errorMessage}
                        </Notification>
                    )}

                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <Title order={3} mb="lg">
                            Infos über Betrieb
                        </Title>

                        <TextInput
                            label="Name"
                            placeholder="Acme Inc."
                            {...form.getInputProps('name')}
                            withAsterisk
                            mb="sm"
                        />
                        <TextInput
                            label="Straße"
                            placeholder="123 AdminMain St"
                            {...form.getInputProps('street')}
                            withAsterisk
                            mb="sm"
                        />
                        <TextInput
                            label="Stadt"
                            placeholder="New York"
                            {...form.getInputProps('city')}
                            withAsterisk
                            mb="sm"
                        />
                        <TextInput
                            label="Bundesland"
                            placeholder="NY"
                            {...form.getInputProps('state')}
                            withAsterisk
                            mb="sm"
                        />
                        <TextInput
                            label="Postleitzahl"
                            placeholder="10001"
                            {...form.getInputProps('zip')}
                            withAsterisk
                            mb="sm"
                        />
                        <TextInput
                            label="Land"
                            placeholder="USA"
                            {...form.getInputProps('country')}
                            withAsterisk
                            mb="sm"
                        />

                        <Group mt="md">
                            <Button variant="outline" onClick={closeDrawer}>
                                Abbrechen
                            </Button>
                            <Button type="submit" variant="filled" loading={loading}>
                                Erstellen
                            </Button>
                        </Group>
                    </form>
                </Box>
            </Drawer>
        </Box>
    );
};

export default CreateOrganization;
